import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cannesEventPriceFaq, cannesEventSeo } from "../slugs/eventFaq";

const mipim: ChauffeurPage = {
  slug: "mipim",
  title: "Private chauffeur MIPIM Cannes – Executive VTC",
  subtitle:
    "Private chauffeur during MIPIM: executive transfers, multi-stop client meetings and client dinners around the Palais des Festivals.",
  keywords: [
    "mipim chauffeur",
    "mipim cannes private chauffeur",
    "executive chauffeur mipim",
  ],
  hero: {
    title: "Private chauffeur during MIPIM",
    subtitle:
      "The world's largest real-estate trade show, in March in Cannes: an executive chauffeur to keep a dense B2B schedule.",
    keywords: [
      "MIPIM chauffeur",
      "executive VTC Cannes",
      "Palais des Festivals chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during MIPIM",
    badges: ["Executive transfers", "Multi-stop meetings", "Client dinners"],
  },
  benefits: {
    heading: "Why an executive chauffeur at MIPIM",
    items: [
      {
        title: "B2B schedule kept",
        description:
          "Stands at the Palais, pavilions, lunches and meetings chain back-to-back: a dedicated chauffeur keeps the tempo with no downtime.",
      },
      {
        title: "Multi-stop roadshows",
        description:
          "Several client meetings a day, in Cannes and around, with the same vehicle and the same chauffeur.",
      },
      {
        title: "Business airport runs",
        description:
          "Nice Côte d'Azur peaks in March: flight tracking, name-board welcome and a direct link to the Croisette hotels.",
      },
      {
        title: "Dinners & networking",
        description:
          "Client evenings, prospecting dinners and partner receptions, with a guaranteed late-night return.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during MIPIM",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Cannes link with flight tracking during the show's peak arrivals in March.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur by the hour or day to chain Palais meetings and business lunches.",
      },
      {
        title: "Client roadshow",
        text: "Optimised multi-stop routes between stands, pavilions, offices and partner hotels.",
      },
      {
        title: "Evenings & dinners",
        text: "Networking receptions, prospecting dinners and Croisette evenings, with a guaranteed late return.",
      },
      {
        title: "Delegation shuttles",
        text: "Multi-vehicle coordination for sales teams and institutional delegations.",
      },
    ],
  },
  locations: {
    heading: "Key points during MIPIM",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Stands, national pavilions and meeting spaces.",
      },
      {
        name: "La Croisette",
        description:
          "Business hotels, terraces and exhibitor-hosted private beaches.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, business arrivals concentrated in March.",
      },
      {
        name: "Sophia Antipolis / hinterland",
        description: "Offices, head offices and meetings outside the centre.",
      },
      {
        name: "Mougins & Mandelieu",
        description: "Client dinners and delegation accommodation.",
      },
    ],
  },
  generic: {
    heading: "MIPIM from an executive chauffeur's seat",
    subheading:
      "In March, the world's largest real-estate trade show packs Cannes with back-to-back B2B meetings.",
    paragraphs: [
      "Every March, MIPIM gathers investors, developers and authorities from across the world at the Palais des Festivals. The schedule is dense: stands, pavilions, lunches and meetings chain along the Croisette, and a transport delay can cost a meeting.",
      "Business arrivals concentrate at Nice Côte d'Azur airport. Flight tracking, a name-board welcome and a direct transfer to the hotel avoid waiting when the show drives demand up.",
      "Beyond the transfer, hourly hire turns the day into a smooth roadshow: several client meetings, lunches and networking dinners chained with the same chauffeur, right through to the late evenings.",
    ],
    bullets: [
      "Multi-stop roadshow",
      "Flight tracking in March",
      "Prospecting dinners",
    ],
  },
  process: {
    heading: "Booking a chauffeur for MIPIM",
    steps: [
      "Share your show dates, your hotel and your list of meetings.",
      "We build the multi-stop route and align the timing with your agenda.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Day and evening hire to keep meetings, lunches and dinners on schedule.",
    ],
  },
  faq: [
    ...cannesEventPriceFaq("en", "during MIPIM"),
    {
      q: "How do I book an executive chauffeur during MIPIM?",
      a: "Book ahead: in early March, demand in Cannes climbs sharply. Send your dates, your hotel and your meetings so we can secure a dedicated chauffeur and prepare a multi-stop route.",
    },
    {
      q: "Can you chain several client meetings in one day?",
      a: "Yes, that is the typical MIPIM use case. Hourly hire keeps the same chauffeur and vehicle for a full roadshow between stands, offices and hotels, without rebooking for each trip.",
    },
    {
      q: "Do you handle Nice airport arrivals during the show?",
      a: "Yes, with flight tracking and a name-board welcome. In March, Nice Côte d'Azur airport sees a peak of business arrivals; we anticipate and adjust the pick-up time if your flight is delayed.",
    },
    {
      q: "Do you cover networking dinners and evenings?",
      a: "Yes. Prospecting dinners and partner receptions run late: the chauffeur waits and guarantees your late-night return to the hotel.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Cannes to Nice Airport",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Chauffeur Cannes to Monaco",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Get a quote",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your executive chauffeur for MIPIM",
    text: "Airport transfers, client roadshows and dinners: book a dedicated chauffeur for the show.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : page événement sans prix, même recette que le Festival.
    ...cannesEventSeo("en", "MIPIM Cannes", "Back-to-back client meetings."),
    canonical: "/en/chauffeur/mipim",
  },
};

export default mipim;
